import type { Key, Fetcher } from '@ori/fetching';
import { useSWR } from '@ori/fetching';
import { useCallback, useMemo, useState } from 'react';

export const useSWRFetch = <D, K extends Key, F extends Fetcher<D, K>>({
  fallback,
  fetcher,
  key,
}: {
  fallback: D;
  fetcher: F;
  key: K;
}) => {
  const formattedKey = JSON.stringify({ key });
  const [currentKey, setCurrentKey] = useState<string | null>(null);
  const matchingKey = currentKey === formattedKey;
  const { data, error } = useSWR<D, Error>(currentKey, fetcher);
  const fetch = useCallback(() => {
    if (!matchingKey) {
      setCurrentKey(formattedKey);
    }
  }, [formattedKey, matchingKey]);

  return useMemo(
    () => ({
      data: data ?? fallback,
      error,
      fetch,
      isLoaded: matchingKey && data != null && error == null,
      isLoading: data == null && error == null,
    }),
    [data, error, fallback, fetch, matchingKey],
  );
};
